// The fixed words a chart and its table print, in English and French: the labels the aggregate service writes
// ("Entities", "Amount (EUR)", "Year", "Other"), the rate table's columns and the "Total" row. A French full report
// prints every column header in French (D416); the schema's own field labels are data and stay as written. The
// numbers never change: only the words around them.

import type { AggregateResult } from './charts';
import type { ReportLanguage } from './report-language';

export interface ChartWords {
  total: string;
  other: string;
  undated: string;
  allMatched: string;
  difference: string;
  inGroup: string;
  /** "(all: 26%)" beside a split's header in a share table. */
  all: string;
}

export const CHART_WORDS: Record<ReportLanguage, ChartWords> = {
  en: {
    total: 'Total',
    other: 'Other',
    undated: 'Undated',
    allMatched: 'All matched entities',
    difference: 'Difference',
    inGroup: 'Entities in group',
    all: 'all',
  },
  fr: {
    total: 'Total',
    other: 'Autre',
    undated: 'Sans date',
    allMatched: 'Toutes les entités trouvées',
    difference: 'Écart',
    inGroup: 'Entités du groupe',
    all: 'toutes',
  },
};

/** The aggregate's fixed labels, by the English word the service writes. */
const FR_LABELS: [RegExp, string][] = [
  [/^Entities$/, 'Entités'],
  [/^Links$/, 'Liens'],
  [/^Appearances$/, 'Apparitions'],
  [/^Year$/, 'Année'],
  [/^Year \((.+)\)$/, 'Année ($1)'],
  [/^Amount \(([A-Z]{3})\)$/, 'Montant ($1)'],
  [/^% of the group$/, '% du groupe'],
  [/^(.+) per entity$/, '$1 par entité'],
  [/^Other$/, 'Autre'],
  [/^Undated$/, 'Sans date'],
  [/^Entity$/, 'Entité'],
  [/^Betweenness \(0–1\)$/, 'Intermédiarité (0–1)'],
  [/^Statements$/, 'Déclarations'],
  [/^Interview$/, 'Entretien'],
  [/^Found$/, 'Trouvé'],
  [/^Partly found$/, 'Partiellement trouvé'],
  [/^Context$/, 'Contexte'],
  [/^Already public$/, 'Déjà public'],
  [/^Not found$/, 'Non trouvé'],
  [/^Organizer$/, 'Organisateur'],
  [/^Role$/, 'Rôle'],
];

/** A chart label in the document's language: the service's English words translated, anything else as written. */
export function chartLabel(label: string, lang: ReportLanguage): string {
  if (lang === 'en') return label;
  for (const [re, to] of FR_LABELS) {
    const m = re.exec(label);
    if (m) return to.replace('$1', chartLabel(m[1] ?? '', lang));
  }
  return label;
}

/** An aggregate with its fixed labels in the document's language (buckets and splits "Other" included). */
export function localizeAggregate<T extends Pick<AggregateResult, 'measureLabel' | 'groupLabel' | 'buckets' | 'splits'>>(
  data: T,
  lang: ReportLanguage,
): T {
  if (lang === 'en') return data;
  return {
    ...data,
    measureLabel: chartLabel(data.measureLabel, lang),
    groupLabel: chartLabel(data.groupLabel, lang),
    buckets: data.buckets.map(b => ({ ...b, label: chartLabel(b.label, lang) })),
    splits: data.splits.map(s => ({ ...s, label: chartLabel(s.label, lang) })),
  };
}
